import { useState } from 'react';
import styled from 'styled-components';
import { buildRemainingComparison, buildVelocityChart, formatHours } from './velocity';

const WIDTH = 760;
const HEIGHT = 188;
const PAD = { top: 10, right: 8, bottom: 26, left: 28 };

const Panel = styled.section`
  margin: 8px 12px 4px;
  padding: 8px 12px 10px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  text-align: left;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px 12px;
  margin-bottom: 4px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const Summary = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 13px;
`;

const ChartSvg = styled.svg`
  display: block;
  width: 100%;
  height: 188px;
`;

const Legend = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  font-size: 13px;
`;

const LegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Swatch = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${(props) => props.$color};
`;

const HoverReadout = styled.p`
  margin: 4px 0 0;
  min-height: 1.2em;
  color: #495057;
  font-size: 12px;
`;

const Remaining = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
`;

const RemainingRow = styled.div`
  display: grid;
  grid-template-columns: minmax(88px, 160px) 1fr 48px 48px;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 13px;
`;

const LaneName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Track = styled.div`
  height: 18px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: ${(props) => props.$percent}%;
  background: ${(props) => props.$color};
`;

const Percent = styled.span`
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`;

const Hours = styled.span`
  text-align: right;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
`;

const yTicksFor = (yMax) => {
  if (yMax <= 4) {
    return Array.from({ length: yMax + 1 }, (_, index) => index);
  }
  return [...new Set([0, Math.round(yMax / 2), yMax])];
};

function VelocityChart(props) {
  const model = buildVelocityChart(props.stacks);
  const remaining = buildRemainingComparison(props.stacks, { filter: props.filter });
  const [hover, setHover] = useState('');
  const yMax = Math.max(1, model.max);
  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const groupW = model.days.length === 0 ? plotW : plotW / model.days.length;
  const laneCount = Math.max(1, model.lanes.length);
  const barW = Math.min(16, Math.max(4, (groupW - 10) / laneCount));
  const groupInner = barW * model.lanes.length;
  const yFor = (value) => PAD.top + plotH - (value / yMax) * plotH;
  const ticks = yTicksFor(yMax);
  const summary = model.total === 1
    ? '1 task completed in the last 14 days'
    : `${model.total} tasks completed in the last 14 days`;

  return <Panel data-cy="velocity-chart" aria-label="Velocity of tasks done per lane">
    <Header>
      <Title>Velocity</Title>
      <Summary>{summary}</Summary>
    </Header>
    <ChartSvg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={summary}>
      {ticks.map((tick) => {
        const y = yFor(tick);
        return <g key={tick}>
          <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y} y2={y} stroke="#e9ecef" />
          <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize="11" fill="#6c757d">{tick}</text>
        </g>;
      })}
      {model.days.map((day, dayIndex) => {
        const groupX = PAD.left + dayIndex * groupW;
        const barsX = groupX + (groupW - groupInner) / 2;
        return <g key={day.key}>
          {model.lanes.map((lane, laneIndex) => {
            const count = model.counts[day.key][lane.id];
            if (count === 0) {
              return null;
            }
            const barHeight = Math.max((count / yMax) * plotH, 2);
            const x = barsX + laneIndex * barW;
            const label = `${lane.name}, ${day.label}: ${count} task${count === 1 ? '' : 's'}`;
            return <rect
              key={lane.id}
              x={x}
              y={yFor(count)}
              width={Math.max(barW - 2, 1)}
              height={barHeight}
              fill={lane.color}
              data-cy="velocity-bar"
              data-lane={lane.name}
              data-day={day.key}
              data-count={count}
              onMouseEnter={() => setHover(label)}
              onMouseLeave={() => setHover('')}
            >
              <title>{label}</title>
            </rect>;
          })}
          <text
            x={groupX + groupW / 2}
            y={HEIGHT - 6}
            textAnchor="middle"
            fontSize="11"
            fill="#6c757d"
          >{day.label}</text>
        </g>;
      })}
    </ChartSvg>
    <Legend>
      {model.lanes.map((lane) =>
        <LegendItem key={lane.id}>
          <Swatch $color={lane.color} />
          <span>{lane.name} ({model.laneTotals[lane.id]})</span>
        </LegendItem>
      )}
    </Legend>
    <HoverReadout>{hover}</HoverReadout>
    <Remaining data-cy="remaining-comparison" aria-label="Percent of open time left per lane">
      <Header>
        <Title as="h3">Left</Title>
        <Summary>
          {remaining.totalLeft === 0
            ? 'Nothing open'
            : `${formatHours(remaining.totalLeft)} still open, split by lane`}
        </Summary>
      </Header>
      {remaining.rows.map((row) =>
        <RemainingRow
          key={row.id}
          data-cy="remaining-lane"
          data-lane={row.name}
          data-percent={row.percentLeft}
          data-hours={row.leftHours}
        >
          <LaneName>{row.name}</LaneName>
          <Track>
            <Fill $percent={row.percentLeft} $color={row.color} />
          </Track>
          <Percent>{row.percentLeft}%</Percent>
          <Hours>{row.timeleft}</Hours>
        </RemainingRow>
      )}
    </Remaining>
  </Panel>;
}

export { VelocityChart };
